import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography
} from '@material-ui/core';
import { CloseOutlined } from '@ant-design/icons';
import * as faceapi from 'face-api.js';
import styled from 'styled-components';
import { loadFaceApiModels } from 'utils/faceApiLoader';

// ── System colors ─────────────────────────────────────────────────
const PRIMARY      = '#ffa300';
const PRIMARY_DARK = '#ff8b00';
const PRIMARY_TEXT = '#7a4000';

// ── Cấu hình nhận diện ───────────────────────────────────────────
const DETECTION_CONFIDENCE  = 0.45;
const MIN_FACE_WIDTH_RATIO  = 0.30;
const MAX_FACE_WIDTH_RATIO  = 0.78;
const STABLE_FRAME_MS       = 300;
const DETECTION_INTERVAL_MS = 120;
const CAPTURE_MAX_WIDTH     = 640;
const CAMERA_ZOOM           = 1.75;
const OVAL_WIDTH_FRAC       = 0.83;
const OVAL_HEIGHT_FRAC      = 0.97;

// ── Styled ───────────────────────────────────────────────────────
const VideoWrap = styled.div`
  position: relative;
  width: min(82vw, 58vh);
  height: min(82vw, 58vh);
  max-width: 500px;
  max-height: 500px;
  margin: 0 auto 8px;
  flex-shrink: 0;
  border-radius: 16px;
  overflow: hidden;
  background: #111;
  box-shadow: inset 0 0 0 2px rgba(255, 163, 0, 0.25);

  .video-zoom-inner {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

const VideoEl = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const OvalOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  &::before {
    content: '';
    width: ${OVAL_WIDTH_FRAC * 100}%;
    height: ${OVAL_HEIGHT_FRAC * 100}%;
    border: 3px solid rgba(255, 255, 255, 0.92);
    border-radius: 50%;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.48),
                0 0 20px rgba(255, 163, 0, 0.5);
  }
`;

function BanIcon({ size = 16, style: s }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" style={{ flexShrink: 0, ...s }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────
export default function FaceLoginModal({
  open,
  onClose,
  onCapture,
  loading,
  errorMessage,
  onClearError,
  title      = 'Xác thực khuôn mặt',
  confirmText,
  loadingText
}) {
  // Callback ref để track video DOM node
  const videoNodeRef = useRef(null);
  const [videoMounted, setVideoMounted] = useState(false);
  const setVideoRef = useCallback((node) => {
    videoNodeRef.current = node;
    setVideoMounted(!!node);
  }, []);

  const streamRef            = useRef(null);
  const detectionIntervalRef = useRef(null);
  const stableSinceRef       = useRef(null);

  const [statusMessage,  setStatusMessage]  = useState('Đang khởi động...');
  const [awaitingResult, setAwaitingResult] = useState(false);
  const [modelsReady,    setModelsReady]    = useState(false);
  const [modelsError,    setModelsError]    = useState(false);
  const [cameraReady,    setCameraReady]    = useState(false);
  const [cameraError,    setCameraError]    = useState(false);

  // ── Reset state khi đóng ─────────────────────────────────────
  useEffect(() => {
    if (!open) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
      stableSinceRef.current = null;
      setAwaitingResult(false);
      setCameraReady(false);
      setCameraError(false);
      setModelsError(false);
      setStatusMessage('Đang khởi động...');
    }
  }, [open]);

  // ── 1. Load models (độc lập với camera) ──────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadFaceApiModels()
      .then(() => { if (!cancelled) setModelsReady(true); })
      .catch((e) => {
        console.error('[FaceLoginModal] model load error', e);
        if (!cancelled) {
          setModelsError(true);
          // Không block camera — chuyển sang chế độ chụp thủ công
          setStatusMessage('Nhận diện tự động không khả dụng. Nhấn nút chụp.');
        }
      });
    return () => { cancelled = true; };
  }, [open]);

  // ── 2. Start camera — ngay khi modal mở & video node mount ───
  //    KHÔNG phụ thuộc vào modelsReady
  useEffect(() => {
    if (!open || !videoMounted) return;
    const video = videoNodeRef.current;
    if (!video) return;

    let cancelled = false;
    setStatusMessage('Đang mở camera...');

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          video.play()
            .then(() => {
              if (!cancelled) {
                setCameraReady(true);
                setStatusMessage('Đưa mặt vào khung oval...');
              }
            })
            .catch(() => {
              if (!cancelled) {
                setCameraError(true);
                setStatusMessage('Không thể phát video camera.');
              }
            });
        };
      })
      .catch((err) => {
        if (!cancelled) {
          setCameraError(true);
          const msg = err?.name === 'NotAllowedError'
            ? 'Bạn chưa cấp quyền truy cập camera. Hãy chọn ảnh từ thiết bị.'
            : 'Không thể truy cập camera. Hãy chọn ảnh từ thiết bị.';
          setStatusMessage(msg);
        }
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoNodeRef.current) videoNodeRef.current.srcObject = null;
    };
  }, [open, videoMounted]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Restart camera sau khi lỗi API (chưa đủ 3 lần) ───────
  useEffect(() => {
    if (!open || !errorMessage || /thất bại\s*3\s*lần/i.test(errorMessage)) return;
    if (!videoMounted || cameraError) return;

    setAwaitingResult(false);
    stableSinceRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    const videoEl = videoNodeRef.current;
    if (!videoEl) return;

    let cancelled = false;
    setCameraReady(false);
    setStatusMessage('Đang mở lại camera...');

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        videoEl.srcObject = stream;
        videoEl.onloadedmetadata = () => {
          videoEl.play().then(() => {
            if (!cancelled) { setCameraReady(true); setStatusMessage('Đưa mặt vào khung oval...'); }
          });
        };
      })
      .catch(() => { if (!cancelled) setStatusMessage('Không thể mở lại camera.'); });

    return () => { cancelled = true; };
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Đóng sau 3 lần thất bại
  useEffect(() => {
    if (errorMessage && /thất bại\s*3\s*lần/i.test(errorMessage)) onClose();
  }, [errorMessage, onClose]);

  // ── 4. Detection loop — cần cả camera lẫn models ─────────────
  useEffect(() => {
    if (!open || !cameraReady || !videoMounted || !modelsReady || loading || awaitingResult) return;
    const video = videoNodeRef.current;
    if (!video) return;

    const runDetection = async () => {
      if (!video.videoWidth || !streamRef.current) return;
      try {
        const detections = await faceapi.detectAllFaces(
          video,
          new faceapi.SsdMobilenetv1Options({ minConfidence: DETECTION_CONFIDENCE })
        );

        const vw   = video.videoWidth;
        const vh   = video.videoHeight;
        const zoom = CAMERA_ZOOM;
        const ovalL = vw / 2 - (OVAL_WIDTH_FRAC  / 2) * (vw / zoom);
        const ovalR = vw / 2 + (OVAL_WIDTH_FRAC  / 2) * (vw / zoom);
        const ovalT = vh / 2 - (OVAL_HEIGHT_FRAC / 2) * (vh / zoom);
        const ovalB = vh / 2 + (OVAL_HEIGHT_FRAC / 2) * (vh / zoom);

        const inOval = detections.filter((d) => {
          const cx = d.box.x + d.box.width  / 2;
          const cy = d.box.y + d.box.height / 2;
          return cx >= ovalL && cx <= ovalR && cy >= ovalT && cy <= ovalB;
        });

        if (inOval.length === 0) {
          stableSinceRef.current = null;
          setStatusMessage('Đưa mặt vào khung oval...');
          return;
        }

        const box          = inOval[0].box;
        const visibleWidth = vw / zoom;

        if (box.width < MIN_FACE_WIDTH_RATIO * visibleWidth) {
          stableSinceRef.current = null; setStatusMessage('Di chuyển gần hơn.'); return;
        }
        if (box.width > MAX_FACE_WIDTH_RATIO * visibleWidth) {
          stableSinceRef.current = null; setStatusMessage('Mặt quá gần. Hãy lùi ra xa.'); return;
        }
        if (box.x < ovalL || box.y < ovalT || box.x + box.width > ovalR || box.y + box.height > ovalB) {
          stableSinceRef.current = null; setStatusMessage('Đưa toàn bộ khuôn mặt vào khung oval.'); return;
        }

        const now = Date.now();
        if (!stableSinceRef.current) stableSinceRef.current = now;
        if (now - stableSinceRef.current < STABLE_FRAME_MS) {
          setStatusMessage('Đang chụp...'); return;
        }

        stableSinceRef.current = null;
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
        captureFace(video, box);
      } catch (err) {
        console.error('[FaceLoginModal] detection', err);
      }
    };

    function captureFace(v, box) {
      const vw = v.videoWidth, vh = v.videoHeight;
      const padX = box.width * 0.6, padY = box.height * 0.6;
      const sx = Math.max(0, box.x - padX);
      const sy = Math.max(0, box.y - padY);
      const sw = Math.min(box.width + padX * 2, vw - sx);
      const sh = Math.min(box.height + padY * 2, vh - sy);
      const scale = Math.min(1, CAPTURE_MAX_WIDTH / sw);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(sw * scale);
      canvas.height = Math.round(sh * scale);
      canvas.getContext('2d').drawImage(v, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (v) v.srcObject = null;
        setAwaitingResult(true);
        setStatusMessage(loadingText || 'Đang xử lý...');
        onClearError?.();
        onCapture(new File([blob], 'face.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    }

    detectionIntervalRef.current = setInterval(runDetection, DETECTION_INTERVAL_MS);
    return () => {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
      stableSinceRef.current = null;
    };
  }, [open, cameraReady, videoMounted, modelsReady, loading, awaitingResult, onCapture, onClearError, loadingText]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chụp thủ công (fallback khi models lỗi) ──────────────────
  const handleManualCapture = useCallback(() => {
    const video = videoNodeRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      video.srcObject = null;
      setAwaitingResult(true);
      setStatusMessage(loadingText || 'Đang xử lý...');
      onClearError?.();
      onCapture(new File([blob], 'face.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  }, [onCapture, onClearError, loadingText]);

  const handleClose = useCallback(() => {
    clearInterval(detectionIntervalRef.current);
    detectionIntervalRef.current = null;
    stableSinceRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoNodeRef.current) videoNodeRef.current.srcObject = null;
    onClose();
  }, [onClose]);

  // Chế độ manual: models lỗi nhưng camera OK
  const isManualMode = modelsError && cameraReady && !awaitingResult;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        style: {
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: `linear-gradient(180deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 56px, #fff 56px)`
        }
      }}
      BackdropProps={{ style: { background: 'rgba(0,0,0,0.6)' } }}>

      {/* Header */}
      <DialogTitle
        disableTypography
        style={{
          position: 'relative',
          color: '#fff',
          fontWeight: 700,
          fontSize: 16,
          padding: '0 20px',
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <span>{title}</span>
        <IconButton
          aria-label="Đóng"
          onClick={handleClose}
          size="small"
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.25)'
          }}>
          <CloseOutlined style={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent
        style={{
          padding: '12px 16px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflowY: 'auto',
          minHeight: 0
        }}>

        {/* Khung camera */}
        <VideoWrap>
          <div
            className="video-zoom-inner"
            style={{
              width:  `${CAMERA_ZOOM * 100}%`,
              height: `${CAMERA_ZOOM * 100}%`,
              ...(awaitingResult ? { opacity: 0, pointerEvents: 'none' } : {})
            }}>
            <VideoEl
              ref={setVideoRef}
              autoPlay
              muted
              playsInline
              style={{ transform: 'scaleX(-1)' }}
            />
          </div>

          {/* Oval chỉ hiện khi camera đang chạy */}
          {!awaitingResult && cameraReady && <OvalOverlay />}

          {/* Overlay xử lý */}
          {awaitingResult && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255,245,220,0.95)', borderRadius: 'inherit'
            }}>
              <Typography style={{ color: PRIMARY_TEXT, fontWeight: 600 }}>
                {loadingText || 'Đang xử lý...'}
              </Typography>
            </div>
          )}
        </VideoWrap>

        {/* Trạng thái */}
        <Typography variant="body2" style={{
          textAlign: 'center', fontWeight: 600,
          color: PRIMARY_TEXT, marginBottom: 8, minHeight: 20
        }}>
          {statusMessage}
        </Typography>

        {/* Lỗi API */}
        {errorMessage && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            fontSize: 12, color: '#b91c1c', marginBottom: 10,
            padding: 8, backgroundColor: '#fef2f2', borderRadius: 6,
            maxWidth: 420, width: '100%'
          }}>
            <BanIcon size={16} style={{ marginTop: 1 }} />
            {errorMessage}
          </div>
        )}

        {/* Nút chụp thủ công — fallback khi models lỗi */}
        {isManualMode && (
          <button
            type="button"
            onClick={handleManualCapture}
            style={{
              marginBottom: 8,
              padding: '9px 28px',
              border: 'none',
              borderRadius: 20,
              background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})`,
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer'
            }}>
            Chụp ảnh
          </button>
        )}

        {/* Gợi ý */}
        {!awaitingResult && cameraReady && !modelsError && (
          <Typography variant="caption" style={{
            display: 'block', textAlign: 'center', color: '#aaa', marginTop: 8
          }}>
            Chỉ 1 người, đưa mặt vào khung oval. Ảnh chụp tự động khi nhận diện thành công.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
